import React, { useState } from 'react';
import {
	Button,
	Typography,
	Box,
	Slider,
} from '@mui/material';
import PopupWithForm from './PopupWithForm';
import axios from 'axios';

const MeasurementsForm = () => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [formData, setFormData] =
		useState({
			underBustCircumference: 75,
			bustCircumference: 86,
			chestCenterDistance: 50,
			breastHeight: 30,
			rtwMeasurement: 36,
			height: 167,
			source:
				process.env.REACT_APP_SOURCE ||
				'development', // Utilizing environment variable
		});

	const minBustCircumference =
		formData.underBustCircumference +
		10;
	const maxBustCircumference =
		formData.underBustCircumference +
		26;

	const [
		probableBustCircumferenceRange,
		setProbableBustCircumferenceRange,
	] = useState<[number, number]>([
		formData.underBustCircumference +
			10,
		formData.underBustCircumference +
			26,
	]);

	const handleUnderBustCircumferenceChange =
		(
			event: Event,
			value: number | number[]
		) => {
			const newUnderBustCircumference =
				value as number;
			setFormData((prevData) => ({
				...prevData,
				underBustCircumference:
					newUnderBustCircumference,
			}));
			setProbableBustCircumferenceRange(
				[
					newUnderBustCircumference +
						10,
					newUnderBustCircumference +
						26,
				]
			);
		};

	const handleBustCircumferenceChange =
		(
			event: Event,
			value: number | number[]
		) => {
			const newBustCircumference =
				value as number;
			setFormData((prevData) => ({
				...prevData,
				bustCircumference:
					newBustCircumference,
			}));
			setProbableBustCircumferenceRange(
				[
					formData.underBustCircumference +
						10,
					formData.underBustCircumference +
						26,
				]
			);
		};

	const handleChestCenterDistanceChange =
		(
			event: Event,
			value: number | number[]
		) => {
			const newChestCenterDistance =
				value as number;
			setFormData((prevData) => ({
				...prevData,
				chestCenterDistance:
					newChestCenterDistance,
			}));
		};

	const handleBreastHeightChange = (
		event: Event,
		value: number | number[]
	) => {
		const newBreastHeight =
			value as number;
		setFormData((prevData) => ({
			...prevData,
			breastHeight: newBreastHeight,
		}));
	};

	const handleRtwMeasurementChange = (
		event: Event,
		value: number | number[]
	) => {
		const newRtwMeasurement =
			value as number;
		setFormData((prevData) => ({
			...prevData,
			rtwMeasurement: newRtwMeasurement,
		}));
	};

	const handleHeightChange = (
		event: Event,
		value: number | number[]
	) => {
		const newHeight = value as number;
		setFormData((prevData) => ({
			...prevData,
			height: newHeight,
		}));
	};

	interface Response {
		message: string;
		code: number;
		data: {
			id: number;
			cupSize: string;
		};
		isStored: boolean;
	}

	const [response, setResponse] =
		useState<Response | null>(null);

	const [isPopupOpen, setIsPopupOpen] =
		useState(false);

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>
	) => {
		e.preventDefault();
		try {
			const apiUrl =
				process.env.REACT_APP_API_URL ||
				'https://e00iiszrtk.execute-api.il-central-1.amazonaws.com/prod/store';
			const res = await axios.post(
				apiUrl,
				formData
			);
			setResponse(res.data);
			setIsPopupOpen(true);
		} catch (error) {
			console.error('Error:', error);
		}
	};

	const handlePopupClose = () => {
		setIsPopupOpen(false);
	};

	return (
		<Box className='flex justify-center items-center min-h-screen'>
			<Box className='w-full max-w-md mx-auto p-4'>
				<Typography
					variant='h4'
					align='center'
					gutterBottom
				>
					Measurements Form
				</Typography>
				<form onSubmit={handleSubmit}>
					<Typography gutterBottom>
						Under Bust Circumference
						(cm) *
					</Typography>
					<Box className='mt-9'>
						<Slider
							value={
								formData.underBustCircumference
							}
							onChange={
								handleUnderBustCircumferenceChange
							}
							min={58}
							max={122}
							marks
							step={1}
							valueLabelDisplay='on'
						/>
					</Box>

					<Typography gutterBottom>
						Bust Circumference (cm) *
					</Typography>
					<Box className='mt-9 relative'>
						<Box
							className='absolute inset-0 pointer-events-none'
							style={{
								background: `linear-gradient(to right, 
							 transparent 0%, 
							 transparent ${
									((probableBustCircumferenceRange[0] -
										58) /
										(122 - 58)) *
									100
								}%,
							 rgba(255, 255, 0, 0.5) ${
									((probableBustCircumferenceRange[0] -
										58) /
										(122 - 58)) *
									100
								}%,
							 rgba(255, 255, 0, 0.5) ${
									((probableBustCircumferenceRange[1] -
										58) /
										(122 - 58)) *
									100
								}%, 
							 transparent ${
									((probableBustCircumferenceRange[1] -
										58) /
										(122 - 58)) *
									100
								}%,
							 transparent 100%)`,
								top: '50%',
								transform:
									'translateY(-50%)',
								height: '8px',
								borderRadius: '4px',
								zIndex: 1,
							}}
						/>
						<Slider
							value={
								formData.bustCircumference
							}
							onChange={
								handleBustCircumferenceChange
							}
							min={58}
							max={122}
							marks
							step={1}
							valueLabelDisplay='on'
						/>

						<Box
							display='flex'
							justifyContent='space-between'
						>
							<Typography
								variant='body2'
								color='text.secondary'
							>
								Recommended Min:{' '}
								{minBustCircumference}{' '}
								cm
							</Typography>
							<Typography
								variant='body2'
								color='text.secondary'
							>
								Recommended Max:{' '}
								{maxBustCircumference}{' '}
								cm
							</Typography>
						</Box>
					</Box>

					<Typography
						variant='body2'
						color='text.secondary'
					>
						Circumference at the fullest
						part of the bust. Mandatory
					</Typography>

					<Typography gutterBottom>
						Chest Center Distance (cm)
					</Typography>
					<Box className='mt-9'>
						<Slider
							value={
								formData.chestCenterDistance
							}
							onChange={
								handleChestCenterDistanceChange
							}
							min={10}
							max={60}
							marks
							step={1}
							valueLabelDisplay='on'
						/>
					</Box>

					<Typography gutterBottom>
						Breast Height (cm)
					</Typography>
					<Box className='mt-9'>
						<Slider
							value={
								formData.breastHeight
							}
							onChange={
								handleBreastHeightChange
							}
							min={10}
							max={60}
							marks
							step={1}
							valueLabelDisplay='on'
						/>
					</Box>

					<Typography gutterBottom>
						RTW Measurement (cm) *
					</Typography>
					<Box className='mt-9'>
						<Slider
							value={
								formData.rtwMeasurement
							}
							onChange={
								handleRtwMeasurementChange
							}
							min={32}
							max={52}
							marks
							step={1}
							valueLabelDisplay='on'
						/>
					</Box>
					<Typography
						variant='body2'
						color='text.secondary'
					>
						Measurement from the
						shoulder to the point under
						the bust with the breast
						gathered. Mandatory for
						simple case
					</Typography>

					<Typography gutterBottom>
						Height (cm)
					</Typography>
					<Box className='mt-9'>
						<Slider
							value={formData.height}
							onChange={
								handleHeightChange
							}
							min={120}
							max={220}
							marks
							step={1}
							valueLabelDisplay='on'
						/>
					</Box>
					<Typography
						variant='body2'
						color='text.secondary'
					>
						User's height. Optional
					</Typography>

					<Button
						type='submit'
						variant='contained'
						fullWidth
					>
						Submit
					</Button>
				</form>
				{/* {response && (
					<Box mt={4}>
						<Typography variant='h6'>
							Response:
						</Typography>
						<Typography>
							Message:{' '}
							{response.message}
						</Typography>
						<Typography>
							Cup Size:{' '}
							{response.data.cupSize}
						</Typography>
						<Typography>
							ID: {response.data.id}
						</Typography>
					</Box>
				)} */}
			</Box>
			<PopupWithForm
				open={isPopupOpen}
				onClose={handlePopupClose}
				title='Response'
			>
				{response && (
					<div>
						<Typography>
							Message:{' '}
							{response.message}
						</Typography>
						<Typography>
							Cup Size:{' '}
							{response.data.cupSize}
						</Typography>
						<Typography>
							ID: {response.data.id}
						</Typography>
					</div>
				)}
			</PopupWithForm>
		</Box>
	);
};

export default MeasurementsForm;
